import { useCallback } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { threadSafeUpdateItem, useThreadSafeLocalStorage } from '../../../shared/hooks/useThreadSafeLocalStorage';
import { getUserIdFromUserSelector } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';

const LAST_VISITED_STORAGE_KEY = 'lastVisited';

export interface UseLastVisitedState {
  isInitialized: boolean;
  getLastVisited: (pageId: string) => moment.Moment | null;
  markPageAsVisited: (pageId: string) => Promise<void>;
}

export interface LastVisited {
  [pageId: string]: string;
}

const initialLastVisited: LastVisited = {};

const markPageVisitedReducer = (prevLastVisited: LastVisited | undefined, pageId: string): LastVisited => {
  return { ...prevLastVisited, [pageId]: moment().toISOString() };
};

const getStorageKeyByUserId = (userId: string) => `${userId}:${LAST_VISITED_STORAGE_KEY}`;

export const markPageAsVisited = async (userId: string, pageId: string) => {
  return await threadSafeUpdateItem<LastVisited>(
    getStorageKeyByUserId(userId),
    prevLastVisited => markPageVisitedReducer(prevLastVisited, pageId),
    { initialValue: initialLastVisited }
  );
};

export const useLastVisited = (): UseLastVisitedState => {
  const userId = useSelector(getUserIdFromUserSelector);

  const storageKey = getStorageKeyByUserId(userId);

  const [isInitialized, lastVisited, update] = useThreadSafeLocalStorage<LastVisited>(storageKey, initialLastVisited);

  const getLastVisited = useCallback(
    (pageId: string): moment.Moment | null => (lastVisited?.[pageId] ? moment(lastVisited[pageId]) : null),
    [lastVisited]
  );

  const markPageAsVisited = useCallback(
    (pageId: string) => update(prevLastVisited => markPageVisitedReducer(prevLastVisited, pageId)),
    [update]
  );

  return { isInitialized, getLastVisited, markPageAsVisited };
};
